/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Role, SignalingClient } from "amazon-kinesis-video-streams-webrtc";
import {
  ChannelRole,
  GetSignalingChannelEndpointCommand,
  GetSignalingChannelEndpointCommandInput,
  GetSignalingChannelEndpointCommandOutput,
  KinesisVideoClient,
  KinesisVideoClientConfig,
} from "@aws-sdk/client-kinesis-video";
import {
  GetIceServerConfigCommand,
  GetIceServerConfigCommandOutput,
  KinesisVideoSignalingClient,
  KinesisVideoSignalingClientConfig,
} from "@aws-sdk/client-kinesis-video-signaling";
import { Const } from "const";
import { Box, Typography, styled, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { v4 as generateUuid } from "uuid";
import { useInterval } from "react-use";
import { formatDate } from "utils/DateTime";
import { WebRTCConnection, shouldConnect } from "utils/WebRtc";

interface WebRTCConnectionResult {
  signalingClient?: SignalingClient;
  peerConnection?: RTCPeerConnection;
  intervalId: number;
  remoteStream?: MediaStream;
}

interface Props {
  /** Connection info */
  connection?: WebRTCConnection;
  /** Action for SDP negotiation finished */
  onNegotiationFinished?: () => void;
  /** Action for connected */
  onConnected?: () => void;
  /** Action for connection failed */
  onConnectionFailed?: (error: unknown) => void;
  /** Action for video display started */
  onVideoStarted?: () => void;
}

/**
 * Component of WebRTC video viewer
 * @param{Props} param0 Properties
 * @return{FC} Component
 */
export const WebRTCViewer: FC<Props> = ({
  connection,
  onNegotiationFinished,
  onConnected,
  onConnectionFailed,
  onVideoStarted,
}: Props) => {
  const handleConnectionstatechange = useCallback((event: Event) => {
    console.log(
      `[${formatDate(
        new Date(),
        "Y-MM-DD HH:mm:ss"
      )}][VIEWER] Connection state change`
    );
    console.log(event);
    console.debug(
      `connectionState: ${connectionResult.current?.peerConnection?.connectionState}`
    );
  }, []);

  const handleIcecandidate = useCallback(
    ({ candidate }: RTCPeerConnectionIceEvent) => {
      if (candidate) {
        console.log("[VIEWER] Generated ICE candidate");

        // When trickle ICE is enabled, send the ICE candidates as they are generated.
        console.log(
          `[${formatDate(
            new Date(),
            "Y-MM-DD HH:mm:ss"
          )}][VIEWER] Sending ICE candidate`
        );
        connectionResult.current?.signalingClient?.sendIceCandidate(candidate);
      } else {
        console.log(
          `[${formatDate(
            new Date(),
            "Y-MM-DD HH:mm:ss"
          )}][VIEWER] All ICE candidates have been generated`
        );
        onNegotiationFinished?.();
      }
    },
    [onNegotiationFinished]
  );

  const handleTrack = useCallback(
    (event: RTCTrackEvent) => {
      console.dir(event);
      console.log(
        `[${formatDate(
          new Date(),
          "Y-MM-DD HH:mm:ss"
        )}][VIEWER] Received remote track`
      );
      console.log(`networkState: ${videoRef.current?.networkState}`);
      console.log(`readyState: ${videoRef.current?.readyState}`);
      if (videoRef.current?.srcObject) {
        return;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = event.streams[0];
        console.log(`networkState: ${videoRef.current?.networkState}`);
        console.log(`readyState: ${videoRef.current?.readyState}`);

        console.log(
          `[${formatDate(
            new Date(),
            "Y-MM-DD HH:mm:ss"
          )}][VIEWER] Video starting`
        );
        videoRef.current
          .play()
          .then(() => {
            console.log(
              `[${formatDate(
                new Date(),
                "Y-MM-DD HH:mm:ss"
              )}][VIEWER] Video started`
            );
            console.log(`networkState: ${videoRef.current?.networkState}`);
            console.log(`readyState: ${videoRef.current?.readyState}`);
            // Start displaying time
            setTimeInterval(Const.cameraClockInterval);
            onVideoStarted?.();
          })
          .catch((error: unknown) => console.log(error));
        if (connectionResult.current) {
          connectionResult.current.remoteStream = event.streams[0];
        }
      }
    },
    [onVideoStarted]
  );

  const disconnect = useCallback(() => {
    if (connectionResult.current) {
      console.log("[VIEWER] Stopping viewer connection");
      if (connectionResult.current.signalingClient) {
        connectionResult.current.signalingClient.close();
        connectionResult.current.signalingClient = undefined;
      }

      if (connectionResult.current.peerConnection) {
        connectionResult.current.peerConnection.close();
        connectionResult.current.peerConnection.removeEventListener(
          "icecandidate",
          handleIcecandidate
        );
        connectionResult.current.peerConnection.removeEventListener(
          "connectionstatechange",
          handleConnectionstatechange
        );
        connectionResult.current.peerConnection.removeEventListener(
          "track",
          handleTrack
        );
        connectionResult.current.peerConnection = undefined;
      }

      if (connectionResult.current.intervalId) {
        window.clearInterval(connectionResult.current.intervalId);
        connectionResult.current.intervalId = 0;
      }
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.oncanplay = null;
      videoRef.current.oncanplaythrough = null;
      videoRef.current.onloadeddata = null;
      videoRef.current.onloadedmetadata = null;
      videoRef.current.onloadstart = null;
    }
  }, [handleConnectionstatechange, handleIcecandidate, handleTrack]);

  useEffect(() => {
    return () => {
      console.debug("WebRTCViewer unmounted");
      disconnect();
    };
  }, [disconnect]);

  const loggingPlayerStatus = () => {
    if (videoRef.current) {
      videoRef.current.oncanplay = (ev) => {
        console.log(
          `[${formatDate(new Date(), "Y-MM-DD HH:mm:ss")}][VIEWER] canplay`
        );
      };
      videoRef.current.oncanplaythrough = (ev) => {
        console.log(
          `[${formatDate(
            new Date(),
            "Y-MM-DD HH:mm:ss"
          )}][VIEWER] canplaythrough`
        );
      };
      videoRef.current.onloadeddata = (ev) => {
        console.log(
          `[${formatDate(new Date(), "Y-MM-DD HH:mm:ss")}][VIEWER] loadeddata`
        );
      };
      videoRef.current.onloadedmetadata = (ev) => {
        console.log(
          `[${formatDate(
            new Date(),
            "Y-MM-DD HH:mm:ss"
          )}][VIEWER] loadedmetadata`
        );
      };
      videoRef.current.onloadstart = (ev) => {
        console.log(
          `[${formatDate(new Date(), "Y-MM-DD HH:mm:ss")}][VIEWER] loadstart`
        );
      };
    }
  };

  useEffect(() => {
    console.debug("WebRTCViewer mounted");
    console.debug("connectionResult.current: %o", connectionResult.current);
    const connect = async (webRtcConnection?: WebRTCConnection) => {
      console.info("connect start");
      console.debug("webRtcConnection: %o", webRtcConnection);
      if (!webRtcConnection) return;

      connectionResult.current = {
        intervalId: 0,
      };

      const kinesisVideoClientConfig: KinesisVideoClientConfig = {
        region: process.env.REACT_APP_CAMERA_REGION,
        credentials: {
          accessKeyId: webRtcConnection.accessKeyId,
          secretAccessKey: webRtcConnection.secretAccessKey,
          sessionToken: webRtcConnection.sessionToken,
        },
      };
      console.debug("kinesisVideoClientConfig: %o", kinesisVideoClientConfig);
      const kinesisVideoClient = new KinesisVideoClient(
        kinesisVideoClientConfig
      );

      // Get signaling channel endpoints
      const endpointsByProtocol: { [key: string]: string } =
        await getSignalingChannelEndpoints(
          webRtcConnection,
          kinesisVideoClient
        );
      console.log("[VIEWER] Endpoints: ", endpointsByProtocol);

      // Create Signaling Client
      const signalingClient = new SignalingClient({
        channelARN: webRtcConnection.channelArn,
        channelEndpoint: endpointsByProtocol.WSS,
        clientId: generateUuid(),
        role: Role.VIEWER,
        region: process.env.REACT_APP_CAMERA_REGION!,
        credentials: {
          accessKeyId: webRtcConnection.accessKeyId,
          secretAccessKey: webRtcConnection.secretAccessKey,
          sessionToken: webRtcConnection.sessionToken,
        },
        systemClockOffset: kinesisVideoClient.config.systemClockOffset,
      });
      connectionResult.current.signalingClient = signalingClient;

      // Get Ice servers
      const iceServers: RTCIceServer[] = await getIceServers(
        webRtcConnection,
        endpointsByProtocol.HTTPS
      );
      console.log("[VIEWER] ICE servers: ", iceServers);

      const configuration: RTCConfiguration = {
        iceServers,
        iceTransportPolicy: "all",
      };
      const peerConnection = new RTCPeerConnection(configuration);
      connectionResult.current.peerConnection = peerConnection;

      // Poll for connection stats
      if (connectionResult.current.intervalId > 0) {
        window.clearInterval(connectionResult.current.intervalId);
        connectionResult.current.intervalId = 0;
      }
      connectionResult.current.intervalId = window.setInterval(
        () => peerConnection.getStats().then(),
        Const.peerConnectionInterval
      );

      signalingClient.on("open", async () => {
        await openSignalingClient();
      });
      signalingClient.on(
        "sdpOffer",
        async (sdpOffer: RTCSessionDescription) => {
          console.log(
            `[${formatDate(
              new Date(),
              "Y-MM-DD HH:mm:ss"
            )}][VIEWER] Received SDP offer`
          );
          console.debug("sdpOffer: %o", sdpOffer);
        }
      );

      signalingClient.on(
        "sdpAnswer",
        async (answer: RTCSessionDescriptionInit) => {
          // Add the SDP answer to the peer connection
          console.log(
            `[${formatDate(
              new Date(),
              "Y-MM-DD HH:mm:ss"
            )}][VIEWER] Received SDP answer`
          );
          console.debug("answer: %o", answer);
          await peerConnection.setRemoteDescription(answer);

          // Callback
          onConnected?.();
        }
      );

      signalingClient.on("iceCandidate", (candidate: RTCIceCandidateInit) => {
        // Add the ICE candidate received from the MASTER to the peer connection
        console.log(
          `[${formatDate(
            new Date(),
            "Y-MM-DD HH:mm:ss"
          )}][VIEWER] Received ICE candidate`
        );
        console.debug("candidate: %o", candidate);
        peerConnection.addIceCandidate(candidate);
      });

      signalingClient.on("close", () => {
        console.log(
          `[${formatDate(
            new Date(),
            "Y-MM-DD HH:mm:ss"
          )}][VIEWER] Disconnected from signaling channel`
        );
      });

      signalingClient.on("error", (error: unknown) => {
        console.error(
          `[${formatDate(
            new Date(),
            "Y-MM-DD HH:mm:ss"
          )}][VIEWER] Signaling client error: `,
          error
        );
        if (onConnectionFailed) {
          onConnectionFailed(error);
        }
      });

      // Send any ICE candidates to the other peer
      // @ts-ignore
      peerConnection.addEventListener("icecandidate", handleIcecandidate);

      peerConnection.addEventListener(
        "connectionstatechange",
        handleConnectionstatechange
      );

      // As remote tracks are received, add them to the remote view
      peerConnection.addEventListener("track", handleTrack);
      console.log(
        `[${formatDate(
          new Date(),
          "Y-MM-DD HH:mm:ss"
        )}][VIEWER] Starting viewer connection`
      );

      // Logging for video element events
      loggingPlayerStatus();

      signalingClient.open();
    };

    const init = async () => {
      if (connectionResult.current) {
        disconnect();
      }

      if (shouldConnect(connection)) {
        try {
          await connect(connection);
        } catch (error) {
          console.error(error);
          onConnectionFailed?.(error);
        }
      }
    };

    init();
  }, [
    connection,
    disconnect,
    handleConnectionstatechange,
    handleIcecandidate,
    handleTrack,
    onConnected,
    onConnectionFailed,
  ]);

  const { t } = useTranslation();
  const theme = useTheme();
  const videoRef = useRef<HTMLVideoElement>(null);
  const connectionResult = useRef<WebRTCConnectionResult>();
  const [streamingDate, setStreamingDate] = useState(t("AWVUI-MSG149"));
  const [timeInterval, setTimeInterval] = useState<number | null>(null);
  useInterval(() => {
    setStreamingDate(formatDate(new Date(), "Y-MM-DD HH:mm:ss"));
  }, timeInterval);

  const openSignalingClient = async () => {
    console.log("[VIEWER] Connected to signaling service");

    // Create an SDP offer to send to the master
    console.log(
      `[${formatDate(
        new Date(),
        "Y-MM-DD HH:mm:ss"
      )}][VIEWER] Creating SDP offer`
    );
    await connectionResult.current?.peerConnection?.setLocalDescription(
      await connectionResult.current?.peerConnection?.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      })
    );

    // When trickle ICE is enabled, send the offer now and then send ICE candidates as they are generated. Otherwise wait on the ICE candidates.
    if (connectionResult.current?.peerConnection?.localDescription) {
      console.log(
        `[${formatDate(
          new Date(),
          "Y-MM-DD HH:mm:ss"
        )}][VIEWER] Sending SDP offer`
      );
      connectionResult.current?.signalingClient?.sendSdpOffer(
        connectionResult.current?.peerConnection?.localDescription
      );
    }
    console.log("[VIEWER] Generating ICE candidates");
  };

  const getSignalingChannelEndpoints = async (
    webRtcConnection: WebRTCConnection,
    videoClient: KinesisVideoClient
  ) => {
    const getSignalingChannelEndpointCommandInput: GetSignalingChannelEndpointCommandInput =
      {
        ChannelARN: webRtcConnection.channelArn,
        SingleMasterChannelEndpointConfiguration: {
          Protocols: ["WSS", "HTTPS"],
          Role: ChannelRole.VIEWER,
        },
      };
    const getSignalingChannelEndpointCommand =
      new GetSignalingChannelEndpointCommand(
        getSignalingChannelEndpointCommandInput
      );
    const getSignalingChannelEndpointResponse: GetSignalingChannelEndpointCommandOutput =
      await videoClient.send(getSignalingChannelEndpointCommand);
    if (!getSignalingChannelEndpointResponse.ResourceEndpointList) {
      throw new Error("Failed in describe getSignalingChannelEndpoint.");
    }
    const endpointsByProtocol: { [key: string]: string } =
      getSignalingChannelEndpointResponse.ResourceEndpointList.reduce<{
        [key: string]: string;
      }>((endpoints, endpoint) => {
        // @ts-ignore
        endpoints[endpoint.Protocol] = endpoint.ResourceEndpoint;
        return endpoints;
      }, {});
    console.log("[VIEWER] Endpoints: ", endpointsByProtocol);
    return endpointsByProtocol;
  };

  const getIceServers = async (
    webRtcConnection: WebRTCConnection,
    resourceEndpoint?: string
  ) => {
    const kinesisVideoSignalingClientConfig: KinesisVideoSignalingClientConfig =
      {
        region: process.env.REACT_APP_CAMERA_REGION,
        credentials: {
          accessKeyId: webRtcConnection.accessKeyId,
          secretAccessKey: webRtcConnection.secretAccessKey,
          sessionToken: webRtcConnection.sessionToken,
        },
        endpoint: resourceEndpoint,
      };
    const kinesisVideoSignalingClient = new KinesisVideoSignalingClient(
      kinesisVideoSignalingClientConfig
    );

    const getIceServerConfigCommand = new GetIceServerConfigCommand({
      ChannelARN: webRtcConnection.channelArn,
    });
    const getIceServerConfigCommandResponse: GetIceServerConfigCommandOutput =
      await kinesisVideoSignalingClient.send(getIceServerConfigCommand);

    const iceServers: RTCIceServer[] = [
      {
        urls: `stun:stun.kinesisvideo.${process.env.REACT_APP_CAMERA_REGION}.amazonaws.com:443`,
      },
    ];
    if (getIceServerConfigCommandResponse.IceServerList) {
      iceServers.push(
        ...getIceServerConfigCommandResponse.IceServerList.filter(
          (i) => i.Uris
        ).map((i) => {
          return {
            urls: i.Uris as string[],
            username: i.Username,
            credential: i.Password,
          };
        })
      );
    }
    return iceServers;
  };

  return (
    <>
      <VideoArea>
        <video style={{ width: "100%" }} ref={videoRef} />
        <DateTimeArea>{streamingDate}</DateTimeArea>
      </VideoArea>
      {connection !== undefined && !shouldConnect(connection) && (
        <MessageArea>
          <Typography color={theme.palette.warning.main} variant="h5">
            {t("AWVUI-MSG96")}
          </Typography>
          <Typography color={theme.palette.warning.main} variant="h5">
            <li>
              {connection?.status === Const.webRtcStatusStreaming
                ? t("AWVUI-MSG97")
                : t("AWVUI-MSG162")}
            </li>
          </Typography>
        </MessageArea>
      )}
    </>
  );
};

const MessageArea = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: theme.palette.text.primary,
  position: "absolute",
  width: "100%",
  height: "100%",
}));

const VideoArea = styled(Box)({
  display: "flex",
  position: "relative",
  flexDirection: "column",
  width: "100%",
});

const DateTimeArea = styled(Box)(({ theme }) => ({
  display: "flex",
  position: "absolute",
  bottom: 0,
  padding: theme.spacing(1),
  color: "white",
  textShadow: `2px  2px 5px ${theme.palette.text.primary} , -2px  2px 5px ${theme.palette.text.primary} , 2px -2px 5px ${theme.palette.text.primary} , -2px -2px 5px ${theme.palette.text.primary}`,
}));
